/**
 * Won-cars domain types — self-contained copy for the admin SPA (ported from
 * apps/auction/src/features/won-cars/types/wonCar.ts). IDs/dates are strings here
 * (api-node serialises Mongo ObjectId / Date to JSON strings). This admin view
 * always runs as the admin role, so it sees every user's won cars.
 */

/** Delivery workflow status of a won car (web parity — exact string values). */
export enum WonCarDeliveryStatus {
  UnderAuctionReview = 'Under Auction Review',
  ReleasedToSupplier = 'Released to Supplier',
  InTransit = 'In Transit',
  AtCustoms = 'At Customs',
  Delivered = 'Delivered',
}

/** The editable financial fields of a won car. */
export interface FinancialValues {
  auctionCommission: number;
  delivery: number;
  certificate: number;
  terminal: number;
  customsClearance: number;
  repair: number;
  sale: number;
}

/** Minimal car display data the table/cards render. */
export interface CarDisplayData {
  ai_brand?: string;
  ai_model?: string;
  brand?: string;
  model?: string;
  make?: string;
  auction_name?: string;
  listing_id?: string;
  thumbnail_image?: string;
  detail_images?: string[];
}

/** Populated bid — the table only reads the winning amount. */
export interface WonCarBid {
  bid: number;
}

/** Populated owner — the admin table shows who won the car. */
export interface WonCarUser {
  _id?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
}

/** Won-car record with populated bid/car/user relations. */
export interface WonCarWithDetails extends Partial<FinancialValues> {
  _id?: string;
  bidId?: string;
  carId?: string;
  userId?: string;
  status?: WonCarDeliveryStatus;
  createdAt?: string;
  updatedAt?: string;
  bid?: WonCarBid | null;
  car?: CarDisplayData | null;
  user?: WonCarUser | null;
}

/** PATCH /won-cars/:id body — partial financials and/or status. */
export type UpdateWonCarDto = Partial<FinancialValues> & { status?: WonCarDeliveryStatus };

/** GET /won-cars response envelope (admin: all records). */
export interface WonCarsListResponse {
  success: boolean;
  wonCars: WonCarWithDetails[];
  total: number;
}

/** POST/PATCH /won-cars response envelope. */
export interface WonCarResponse {
  success: boolean;
  wonCar: WonCarWithDetails;
}
